import { defineAsyncComponent } from 'vue'

/**
 * Lazily-mounted modals used to fail SILENTLY: `defineAsyncComponent` with no
 * `onError`/`errorComponent` renders nothing when its chunk cannot be fetched,
 * the `v-if` flag stays `true`, and the button that opened it looks permanently
 * dead. App Review hit exactly that on 2026-07-14 ("the Upgrade to Pro and
 * Export buttons were all unresponsive") — those were the only two lazily
 * mounted modals in the user menu.
 *
 * Everything here is deliberately dependency-free and side-effect-free at import
 * time so it can be unit-tested without a Vue app.
 */

export const CHUNK_LOAD_FAILED_EVENT = 'linkflare-chunk-load-failed'

export const LAZY_COMPONENT_TIMEOUT_MS = 20000
export const LAZY_COMPONENT_RETRIES = 1
export const LAZY_COMPONENT_RETRY_DELAY_MS = 400

/**
 * Announce an unrecoverable chunk failure so the shell can tell the user
 * something went wrong and offer a reload. Never throws: this runs from a Vue
 * error hook, and a throw here would replace a visible failure with a silent one.
 */
export function notifyChunkLoadFailure(name, target = typeof window === 'undefined' ? null : window) {
  if (!target || typeof target.dispatchEvent !== 'function') return false
  try {
    target.dispatchEvent(new CustomEvent(CHUNK_LOAD_FAILED_EVENT, { detail: { name } }))
    return true
  } catch {
    return false
  }
}

/**
 * Vue's `onError` contract, extracted so the retry/give-up policy is testable
 * without mounting anything.
 */
export function createLazyErrorHandler(name, {
  retries = LAZY_COMPONENT_RETRIES,
  retryDelayMs = LAZY_COMPONENT_RETRY_DELAY_MS,
  notify = notifyChunkLoadFailure,
  schedule = (fn, delay) => setTimeout(fn, delay)
} = {}) {
  return (error, retry, fail, attempts) => {
    if (attempts <= retries) {
      // One transient network blip should not cost the user the feature.
      schedule(retry, retryDelayMs)
      return
    }

    console.error(`[Linkflare] Failed to load "${name}" chunk`, error)
    notify(name)
    fail(error)
  }
}

/**
 * Drop-in replacement for `defineAsyncComponent(loader)` that retries once and,
 * if it still cannot load, surfaces the failure instead of rendering nothing.
 *
 * `errorComponent` renders nothing on purpose — the modal it stands in for has
 * no meaningful degraded state — but it MUST exist, because `timeout` is only
 * honoured when one is provided. The user-visible signal is the toast raised by
 * `CHUNK_LOAD_FAILED_EVENT`.
 */
export function lazyComponent(name, loader, options = {}) {
  return defineAsyncComponent({
    loader,
    timeout: options.timeout ?? LAZY_COMPONENT_TIMEOUT_MS,
    errorComponent: { name: `${name}LoadFailed`, render: () => null },
    onError: createLazyErrorHandler(name, options)
  })
}
